import type {
  AiAnswerVoteBody,
  AiCompareLayout,
  AiPlaceLayout,
  AiRideLayout,
  ConciergeAsk,
  ConciergeAskOption,
  ConciergeTimes,
  EditorialBlock,
  Lang,
} from "@majourneys/shared";

export type {
  AiAnswerVoteBody,
  AiCompareLayout,
  AiPlaceLayout,
  AiRideLayout,
  ConciergeAsk,
  ConciergeAskOption,
  ConciergeTimes,
};
import { apiBaseAbsolute } from "../../../lib/api-base";

export type AiMatch = {
  id: string;
  slug: string;
  title: string | null;
  subtitle: string | null;
  overview: string | null;
  citySlug: string | null;
  cityName: string | null;
  categorySlug: string | null;
  categoryName: string | null;
  segment: string | null;
  mode: "bookable" | "explore" | null;
  heroImageUrl: string | null;
  similarity: number;
  /**
   * Where the place is, when we know. Explore rows carry coordinates; bookable
   * tours are sold as a meeting time rather than an address and come back
   * null. Anything plotting these must say how many it could not place rather
   * than quietly drawing a thinner map than the answer.
   */
  latitude?: number | null;
  longitude?: number | null;
  /** Lowest per-person price in MAD; null/absent for explore or unpriced rows. */
  priceMad?: number | null;
  /**
   * What `priceMad` is charged per, `prices.type`: 0 person, 1 hour, 2 night,
   * 3 vehicle or group. Absent on answers from before the API sent it.
   */
  priceType?: number | null;
};

export type AiChatTurn = { role: "user" | "assistant"; content: string };

/* ---------- Structured response shapes ---------- */

/**
 * Discriminator over the rendering shape of an assistant reply.
 *  - `qa` (default): text answer + GlassCardStack of matches.
 *  - `itinerary`: text answer + day-by-day grid built from matches.
 *  - `budget`: text answer + per-item price list under a running spend bar.
 *  - `theme`: text answer + narrative multi-stop journey card.
 *  - `ride`: one short sentence + a card per transfer route and its vehicles.
 *  - `place`: one named venue as a single card.
 *  - `compare`: two or three named venues side by side, then our pick.
 *
 * Set by the backend after intent classification. The frontend switches
 * the assistant turn's renderer based on this field.
 */
export type AiResponseType =
  | "qa"
  | "itinerary"
  | "budget"
  | "theme"
  | "ride"
  | "place"
  | "compare";

export type BudgetTier = "backpacker" | "midrange" | "premium" | "luxury";

export type BudgetItem = {
  matchSlug: string;
  priceMad: number | null;
  kept: boolean;
  reason?: string;
};

export type Budget = {
  totalMad: number;
  dailyMad?: number;
  usedMad: number;
  travelers: number;
  tier: BudgetTier;
  assumptionsLine: string;
  items: BudgetItem[];
  savingsTip?: string;
};

export type ItinerarySlot = {
  /** Visible slot label, e.g. "Morning", "Afternoon", "Evening". */
  time: string;
  /**
   * The row this stop books, when we sell it. Absent on a place we do not.
   *
   * This was typed as required while the API had already been emitting
   * card-less stops, so the renderer's `matchBySlug.get(s.matchSlug)` looked
   * total and was not. Mirrors the API's own ItinerarySlot.
   */
  matchSlug?: string;
  /** A stop with no card behind it: a name, a line, nothing to tap. */
  place?: { title: string; note?: string };
  /** What the plan says about this stop, shown under a card's title. */
  note?: string;
  /** Optional one-line transit hint shown above the slot. */
  transit?: string;
};

export type ItineraryDay = {
  /** Display label, e.g. "Day 1". */
  label: string;
  /** Optional human date, e.g. "Tue, May 28". */
  date?: string;
  /** Primary city for the day (label-only; from cityName). */
  city?: string;
  slots: ItinerarySlot[];
};

export type Itinerary = {
  days: ItineraryDay[];
  /** Total booked hours across all slots (rough estimate). */
  totalHours?: number;
  /** Sum of per-pick prices in MAD (excludes free items). */
  totalPriceMad?: number;
  /** Distinct cities visited, in order. */
  cities?: string[];
};

/**
 * A one-tap answer to a follow-up question the assistant asked, e.g. the
 * cities that actually stock the activity the user named.
 *
 * Derived server-side from real catalog rows, so a chip can never lead to an
 * empty result — unlike the heuristic chips, which guess from a static list.
 */
export type AiSuggestion = {
  /** Short display text, e.g. "Marrakech". */
  label: string;
  /** Full query to send when tapped, e.g. "quad biking in Marrakech". */
  value: string;
  kind: "city";
};

/**
 * A next question the model wrote for the answer it just gave. Distinct from
 * `AiSuggestion`, which answers a question the model asked.
 */
export type AiFollowUp = { label: string; value: string };

/**
 * A catalogue row or city named in a markdown answer, one per
 * `[[slug|Label]]` marker. Only sent alongside `format: "markdown"`.
 */
export type AiEntity = {
  slug: string;
  /** The exact text as it appears in the answer. */
  label: string;
  kind: "activity" | "city";
  latitude: number | null;
  longitude: number | null;
};

/**
 * Chat kit fields, shared by the blocking response and the stream's `done`
 * frame. All optional: an older API sends none of them.
 */
export type AiChatKitFields = {
  /**
   * A structured question back to the guest: tappable options, or a date and
   * party picker. The same question is always in `answer` too.
   */
  ask?: ConciergeAsk;
  /** The question was not about travel and was refused. Comes with `ask`. */
  offTopic?: true;
  /** Open start times from an availability check. No capacity figure. */
  times?: ConciergeTimes;
  /** Id of this answer, for `voteAnswer`. */
  turnId?: string;
  /** Places named in a markdown answer. */
  entities?: AiEntity[];
  /** The cards are the nearest thing we have, not a match for the request. */
  alternatives?: true;
};

/**
 * Which answer shape to ask the API for.
 *
 * "markdown" gets the document the phone renders: `##` sections, `- bullets`,
 * `**bold**` and `[[slug|Label]]` entity markers. Omitting it gets one
 * unstructured paragraph, which is what the web asked for until 2026-09-08 and
 * why a browse answer arrived as a fifteen-line wall of text.
 */
export type AiAnswerFormat = "plain" | "markdown" | "editorial";

/* ---------- Free-question allowance ---------- */

/**
 * How many more questions this visitor may ask before the gate closes.
 * `"unlimited"` for a signed-in user.
 *
 * Read off the `x-mj-free-remaining` response header, NEVER counted here and
 * never written to localStorage — the whole point of the feature is that it
 * survives someone clearing site data, so the server's number is the only
 * number.
 */
export type FreeRemaining = number | "unlimited";

export const FREE_REMAINING_HEADER = "x-mj-free-remaining";

/** Identifies this caller to the API's free-answer gate. */
const CLIENT_HEADER = "x-mj-client";
const WEB_CLIENT = "web";

export function parseFreeRemaining(headers: Headers): FreeRemaining | null {
  const raw = headers.get(FREE_REMAINING_HEADER);
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (trimmed === "unlimited") return "unlimited";
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.trunc(n));
}

/**
 * The allowance is spent: HTTP 403 with body `{"error":"signin_required"}`.
 *
 * This is a plain JSON refusal, not an error frame inside a stream, so it is
 * detected from the response status before any attempt to read a body as SSE.
 */
export class ConciergeSignInRequiredError extends Error {
  readonly code = "signin_required";
  readonly remaining: number;

  constructor(remaining = 0) {
    super("signin_required");
    this.name = "ConciergeSignInRequiredError";
    this.remaining = remaining;
  }
}

/**
 * Brand check rather than a bare `instanceof`: a test that mocks this module,
 * or two copies of it in one bundle, would break identity on the class while
 * the thrown value is still the same refusal.
 */
export function isSignInRequired(err: unknown): boolean {
  if (err instanceof ConciergeSignInRequiredError) return true;
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { code?: unknown }).code === "signin_required"
  );
}

/**
 * Turns the gate's 403 into `ConciergeSignInRequiredError`. Any other 403 is
 * a real failure and keeps its generic error.
 */
async function throwIfGated(res: Response, label: string): Promise<void> {
  if (res.status !== 403) return;
  const body = (await res.json().catch(() => null)) as
    | { error?: string; remaining?: number }
    | null;
  if (body?.error === "signin_required") {
    throw new ConciergeSignInRequiredError(
      typeof body.remaining === "number" ? body.remaining : 0,
    );
  }
  throw new Error(`${label} 403: ${JSON.stringify(body ?? {}).slice(0, 200)}`);
}

/**
 * A booking the assistant has put together, awaiting the traveller's confirm.
 * Nothing is reserved: Confirm opens the place's booking form with these
 * answers filled in, and that form owns the only write.
 */
export type BookingProposal = {
  slug: string;
  title: string;
  city: string | null;
  date: string;
  time: string;
  adults: number;
  children: number;
  /** Null for a restaurant table (always free) or when no price is published. */
  priceMad: number | null;
  payOnArrival: true;
  citySlug?: string | null;
  categorySlug?: string | null;
  segment?: string | null;
  /** Per-night stays: how many nights, and the day they leave. */
  nights?: number;
  checkOut?: string;
  /** The option chosen (a spa treatment), when matched to the place's list. */
  packageId?: string;
  packageName?: string;
  /** Spas: who is coming. */
  women?: number;
  men?: number;
};

/** One place to sleep in the chat's stay list. */
export type StayRow = {
  slug: string;
  title: string;
  /** The whole stay for the party, in `currency`; null when undated or full. */
  totalPrice: number | null;
  currency?: string;
  starRating?: number | null;
  propertyType?: string | null;
  image?: string | null;
};

/** Stays found this turn, listed under the reply (design B, 2026-10-05). */
export type StayShelf = {
  city: string;
  citySlug: string;
  checkin: string | null;
  checkout: string | null;
  adults: number | null;
  items: StayRow[];
};

export type AiChatResponse = AiChatKitFields & {
  answer: string;
  matches: AiMatch[];
  /**
   * Off the `x-mj-free-remaining` header, not the JSON body. `null` when the
   * header was absent (an older API), which leaves the gate untouched.
   */
  freeRemaining?: FreeRemaining | null;
  /** Present when the assistant asked a follow-up question. */
  suggestions?: AiSuggestion[];
  /**
   * True when the backend decided the query was too vague to answer with
   * cards. The frontend should render the text answer only — no card
   * stack — and surface refinement chips.
   */
  clarify?: boolean;
  /**
   * Shape the assistant turn should be rendered as. Defaults to "qa".
   */
  type?: AiResponseType;
  /** Present iff `type === "itinerary"`. */
  itinerary?: Itinerary;
  /** Present iff `type === "budget"`. */
  budget?: Budget;
  /** Present iff `type === "ride"`. */
  ride?: AiRideLayout;
  /** Present iff `type === "place"`. */
  place?: AiPlaceLayout;
  /** Present iff `type === "compare"`. */
  compare?: AiCompareLayout;
  /** Shape of `answer` — "markdown" when we asked for the document. */
  format?: AiAnswerFormat;
  /**
   * The model-written voice layer of an editorial answer: kicker, headline,
   * lede, fact pills, themed sections, a line per place, the estimate and the
   * three advice slots. Words only — every fact on screen still comes from
   * the grounded rows.
   */
  editorial?: EditorialBlock;
  /** The model's own next questions for this answer. */
  followUps?: AiFollowUp[];
  /** Present when the assistant proposed a booking this turn. */
  bookingProposal?: BookingProposal;
  /** Present when the assistant looked for somewhere to stay. */
  stays?: StayShelf;
};

/* ---------- Streaming event shapes (POST /ai/chat/stream) ---------- */

/**
 * Emitted once, as soon as retrieval completes and before the model has
 * written anything. Carries everything needed to render the cards.
 */
export type AiStreamMatches = {
  type: "matches";
  matches: AiMatch[];
  clarify: boolean;
  answerType?: AiResponseType;
  itinerary?: Itinerary;
  budget?: Budget;
  suggestions?: AiSuggestion[];
  format?: AiAnswerFormat;
  followUps?: AiFollowUp[];
  editorial?: EditorialBlock;
};

/**
 * What the concierge is doing right now, while it does it.
 *
 * The API has emitted these from the start — one per tool the agent runs, so a
 * live turn sends "Marrakech", "Looked through 41 places", "Writing the
 * answer" — and the web client dropped every one of them on the floor. The
 * phone showed the work; the website showed a spinner.
 *
 * It matters most now that the wait is real but short. Six to eight seconds of
 * a spinner reads as broken; six to eight seconds of visible work reads as
 * work.
 */
export type AiStreamProgress = {
  type: "progress";
  /** Already in the guest's language. */
  text: string;
  /**
   * Which kind of work: "search", "found", "details", "cities",
   * "availability", "booking", "stays", "weather", "ask" or "working".
   * Absent on older APIs.
   */
  step?: string;
};

export type AiStreamEvent =
  | AiStreamMatches
  | AiStreamProgress
  | { type: "delta"; text: string }
  /**
   * The finished answer, and the only frame that carries the answer's SHAPE.
   *
   * `format`, `followUps`, `answerType` and `itinerary` arrive here and
   * nowhere else — the API notes the phone "reads `done` and nothing else".
   * The web read `answer` alone, so a markdown answer looked like plain text
   * and printed its own `[[slug|Label]]` markers to the reader.
   */
  | (AiChatKitFields & {
      type: "done";
      answer: string;
      /** A clarifying or refused turn (the free allowance is not spent). */
      clarify?: boolean;
      format?: AiAnswerFormat;
      followUps?: AiFollowUp[];
      answerType?: AiResponseType;
      itinerary?: Itinerary;
      budget?: Budget;
      ride?: AiRideLayout;
      place?: AiPlaceLayout;
      compare?: AiCompareLayout;
      editorial?: EditorialBlock;
      bookingProposal?: BookingProposal;
      stays?: StayShelf;
    })
  | { type: "error"; message: string };

export type AiChatRequest = {
  query: string;
  history?: AiChatTurn[];
  lang?: Lang;
  limit?: number;
  /** Absent stays absent — the API must not read "plain" as an opt-in. */
  format?: AiAnswerFormat;
};

const CHAT_TIMEOUT_MS = 30_000;

/**
 * The AI routes are mounted outside the Eden Treaty type chain (see
 * `apps/api/src/index.ts`), so we hit them with plain fetch instead of
 * the typed client. Keeps the API type-recursion budget free.
 */
export async function aiChat(
  body: AiChatRequest,
  init?: { signal?: AbortSignal },
): Promise<AiChatResponse> {
  const base = apiBaseAbsolute();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CHAT_TIMEOUT_MS);
  const signal = init?.signal
    ? mergeSignals(init.signal, controller.signal)
    : controller.signal;

  try {
    const res = await fetch(`${base}/ai/chat`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        // Names this caller as the web app. The gate on the API only counts
        // clients that ask for it, so the phone app, which calls these same
        // routes, is left alone until it sends this too.
        [CLIENT_HEADER]: WEB_CLIENT,
      },
      body: JSON.stringify(body),
      // The allowance is per person, so the API has to see the session
      // cookie — without this a signed-in traveller is billed as anonymous
      // and hits the gate they already paid their email for.
      credentials: "include",
      signal,
    });
    await throwIfGated(res, "ai/chat");
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`ai/chat ${res.status}: ${text.slice(0, 200)}`);
    }
    const json = (await res.json()) as AiChatResponse;
    return { ...json, freeRemaining: parseFreeRemaining(res.headers) };
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Streamed counterpart to `aiChat`, consuming POST /ai/chat/stream.
 *
 * The payoff is `onMatches`: retrieval finishes long before the model does
 * (roughly 0.6s vs 6s for a detail question), so the cards render while the
 * answer is still being written instead of after it.
 *
 * Falls back to the blocking endpoint if the response is not a stream — an
 * older API, or a proxy that collapsed it — so a deploy-order mismatch
 * degrades to today's behaviour rather than an empty chat.
 */
export async function aiChatStream(
  body: AiChatRequest,
  handlers: {
    onMatches?: (e: AiStreamMatches) => void;
    /** One line of narration per tool the agent runs. See AiStreamProgress. */
    onProgress?: (text: string, step?: string) => void;
    onDelta?: (text: string) => void;
  },
  init?: { signal?: AbortSignal },
): Promise<AiChatResponse> {
  const base = apiBaseAbsolute();
  // No timeout race here: a stream is legitimately long-lived, and the
  // caller's abort signal is the correct way to end it early.
  const signal = init?.signal;

  let res: Response;
  try {
    res = await fetch(`${base}/ai/chat/stream`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        // Names this caller as the web app. The gate on the API only counts
        // clients that ask for it, so the phone app, which calls these same
        // routes, is left alone until it sends this too.
        [CLIENT_HEADER]: WEB_CLIENT,
      },
      body: JSON.stringify(body),
      // See aiChat: the allowance is keyed to the session, so the cookie has
      // to travel with the request.
      credentials: "include",
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    if (signal?.aborted) throw err;
    return aiChat(body, init ?? {});
  }

  // Branch on the refusal BEFORE touching res.body. A spent allowance comes
  // back as plain JSON with no stream behind it, so reading it as SSE would
  // hang on a body that never produces a frame.
  await throwIfGated(res, "ai/chat/stream");

  const freeRemaining = parseFreeRemaining(res.headers);

  const isStream =
    res.ok && !!res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream");
  if (!isStream) {
    if (res.status === 404 || res.status === 405) return aiChat(body, init ?? {});
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`ai/chat/stream ${res.status}: ${text.slice(0, 200)}`);
    }
    const json = (await res.json()) as AiChatResponse;
    return { ...json, freeRemaining };
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffered = "";
  let answer = "";
  let matches: AiMatch[] = [];
  let clarify: boolean | undefined;
  let type: AiResponseType | undefined;
  let itinerary: Itinerary | undefined;
  let budget: Budget | undefined;
  let ride: AiRideLayout | undefined;
  let bookingProposal: BookingProposal | undefined;
  let stays: StayShelf | undefined;
  let place: AiPlaceLayout | undefined;
  let compare: AiCompareLayout | undefined;
  let suggestions: AiSuggestion[] | undefined;
  let format: AiAnswerFormat | undefined;
  let followUps: AiFollowUp[] | undefined;
  let editorial: EditorialBlock | undefined;
  let ask: ConciergeAsk | undefined;
  let offTopic: true | undefined;
  let times: ConciergeTimes | undefined;
  let turnId: string | undefined;
  let entities: AiEntity[] | undefined;
  let alternatives: true | undefined;
  let streamError: string | null = null;

  const handleEvent = (event: AiStreamEvent) => {
    switch (event.type) {
      case "matches":
        matches = event.matches;
        clarify = event.clarify;
        type = event.answerType;
        itinerary = event.itinerary;
        budget = event.budget;
        suggestions = event.suggestions;
        handlers.onMatches?.(event);
        break;
      case "progress":
        // Narration only. It never becomes part of the answer, so it is handed
        // straight to the caller and not accumulated.
        handlers.onProgress?.(event.text, event.step);
        break;
      case "delta":
        answer += event.text;
        handlers.onDelta?.(event.text);
        break;
      case "done":
        // The server's full text is authoritative over our concatenation.
        answer = event.answer;
        // A refused or clarifying turn says so here too, not only on matches.
        if (event.clarify != null) clarify = event.clarify;
        if (event.ask) ask = event.ask;
        if (event.offTopic) offTopic = true;
        if (event.times) times = event.times;
        if (event.turnId) turnId = event.turnId;
        if (event.entities) entities = event.entities;
        if (event.alternatives) alternatives = true;
        if (event.format) format = event.format;
        if (event.followUps) followUps = event.followUps;
        if (event.answerType) type = event.answerType;
        if (event.itinerary) itinerary = event.itinerary;
        if (event.budget) budget = event.budget;
        if (event.ride) ride = event.ride;
        if (event.place) place = event.place;
        if (event.compare) compare = event.compare;
        if (event.bookingProposal) bookingProposal = event.bookingProposal;
        if (event.stays) stays = event.stays;
        // A ride replaces the prose, and the header written for that prose
        // goes with it; the done frame is the only one that knows.
        editorial = event.editorial;
        break;
      case "error":
        streamError = event.message;
        break;
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffered += decoder.decode(value, { stream: true });
      // SSE frames are blank-line delimited and split across arbitrary
      // chunk boundaries, so an incomplete frame has to carry over.
      let sep: number;
      while ((sep = buffered.indexOf("\n\n")) !== -1) {
        const frame = buffered.slice(0, sep);
        buffered = buffered.slice(sep + 2);
        for (const line of frame.split("\n")) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === "[DONE]") continue;
          try {
            handleEvent(JSON.parse(payload) as AiStreamEvent);
          } catch {
            // A malformed frame should not discard a working stream.
          }
        }
      }
    }
  } finally {
    await reader.cancel().catch(() => {});
  }

  if (streamError && !answer) throw new Error(streamError);

  return {
    answer,
    matches,
    freeRemaining,
    ...(clarify != null ? { clarify } : {}),
    ...(type ? { type } : {}),
    ...(itinerary ? { itinerary } : {}),
    ...(budget ? { budget } : {}),
    ...(ride ? { ride } : {}),
    ...(place ? { place } : {}),
    ...(compare ? { compare } : {}),
    ...(suggestions ? { suggestions } : {}),
    ...(format ? { format } : {}),
    ...(followUps ? { followUps } : {}),
    ...(editorial ? { editorial } : {}),
    ...(bookingProposal ? { bookingProposal } : {}),
    ...(stays ? { stays } : {}),
    ...(ask ? { ask } : {}),
    ...(offTopic ? { offTopic } : {}),
    ...(times ? { times } : {}),
    ...(turnId ? { turnId } : {}),
    ...(entities ? { entities } : {}),
    ...(alternatives ? { alternatives } : {}),
  };
}

/**
 * Thumbs up or down on one answer (chat kit C29), by the `turnId` on its
 * done frame. Open to anonymous guests; the cookie travels so a signed-in
 * guest's vote is tied to their account. Voting again replaces the vote.
 *
 * Resolves true when recorded. Never throws: a vote that fails to land is not
 * worth an error in the chat.
 */
export async function voteAnswer(
  turnId: string,
  body: AiAnswerVoteBody,
  init?: { signal?: AbortSignal },
): Promise<boolean> {
  if (!turnId) return false;
  try {
    const res = await fetch(`${apiBaseAbsolute()}/ai/answers/${encodeURIComponent(turnId)}/vote`, {
      method: "POST",
      headers: { "content-type": "application/json", [CLIENT_HEADER]: WEB_CLIENT },
      body: JSON.stringify(body),
      credentials: "include",
      ...(init?.signal ? { signal: init.signal } : {}),
    });
    return res.ok;
  } catch {
    return false;
  }
}

function mergeSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  if (a.aborted) return a;
  if (b.aborted) return b;
  const c = new AbortController();
  const onA = () => c.abort(a.reason);
  const onB = () => c.abort(b.reason);
  a.addEventListener("abort", onA, { once: true });
  b.addEventListener("abort", onB, { once: true });
  return c.signal;
}
